import { dialogueEmail } from "@canucci/base/env";
import {
  accentDividerStyle,
  filledCtaButtonProps,
  leftAccentCardProps,
  surfaceStrongHighlightCardProps,
  surfaceStatusIconProps,
} from "@canucci/base-mantine/theme";
import { Anchor, Box, Button, Container, Flex, Paper, SimpleGrid, Stack, Text, ThemeIcon, Title } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { FaChartLine, FaClipboardCheck, FaComments } from "react-icons/fa";
import { AiOutlineInfoCircle } from "react-icons/ai";
import { Link } from "react-router-dom";
import React from "react";

function Feature({ icon, title, text }: { icon: React.ElementType; title: string; text: string }) {
  return (
    <Paper {...leftAccentCardProps} p="md">
      <Flex direction="column" align="center" ta="center">
        <ThemeIcon {...surfaceStatusIconProps} size={48} radius="md" mb="md">
          {React.createElement(icon, { size: 24 })}
        </ThemeIcon>
        <Title size="h4" mb="xs" c="var(--text-on-dark)">
          {title}
        </Title>
        <Text c="var(--text-on-dark)">{text}</Text>
      </Flex>
    </Paper>
  );
}

function GeneralRootPage() {
  const { t } = useTranslation("pages");
  const { t: tCommon } = useTranslation("common");

  return (
    <Box>
      <Container size="lg" ta="center" pt={{ base: "2rem", md: "4rem" }} pb={{ base: "0.25rem", md: "0.5rem" }}>
        <Title order={1} size="h1" mb="sm" c="var(--text-on-dark)">
          {t("general.root.hero.title")}
        </Title>
        <Box style={accentDividerStyle} mx="auto" mb="md" />
        <Text fz="xl" mb="xl" c="var(--text-on-dark)">
          {t("general.root.hero.subtitle")}
        </Text>
      </Container>

      <Container size="lg" pt={{ base: "0.5rem", md: "1rem" }} pb={{ base: "0.25rem", md: "0.5rem" }}>
        <Stack gap="md" component="section">
          <Title order={2} size="h2" ta="center" c="var(--accent-on-dark)" fw="bold">
            {t("general.root.features.title")}
          </Title>
          <Text fz="lg" c="var(--text-on-dark)" ta="center" maw="42rem" mx="auto">
            {t("general.root.features.subtitle")}
          </Text>
        </Stack>

        <SimpleGrid cols={{ base: 1, md: 3 }} spacing="xl" mt="xl">
          <Feature
            icon={FaComments}
            title={t("general.root.features.feature1.title")}
            text={t("general.root.features.feature1.text")}
          />
          <Feature
            icon={FaChartLine}
            title={t("general.root.features.feature2.title")}
            text={t("general.root.features.feature2.text")}
          />
          <Feature
            icon={FaClipboardCheck}
            title={t("general.root.features.feature3.title")}
            text={t("general.root.features.feature3.text")}
          />
        </SimpleGrid>
      </Container>

      <Box>
        <Container size="lg" py={{ base: "2rem", md: "4rem" }} ta="center">
          <Paper {...surfaceStrongHighlightCardProps} p="md" ta="left">
            <Flex align="center" direction={{ base: "column", md: "row" }}>
              <ThemeIcon {...surfaceStatusIconProps} size={48} mr={{ base: 0, md: "xl" }} mb={{ base: "md", md: 0 }}>
                <AiOutlineInfoCircle size={32} />
              </ThemeIcon>
              <Box>
                <Title order={3} size="h3" mb="xs" c="var(--text-on-highlight)">
                  {t("general.root.access.title")}
                </Title>
                <Text fz="lg" c="var(--text-on-highlight)">
                  {t("general.root.access.subtitle")}
                </Text>
              </Box>
            </Flex>
          </Paper>

          <Box mt="xl">
            <Stack gap="md" py="md" mb="xl">
              <Text fz="lg" c="var(--accent-on-dark)" fw="bold">
                {tCommon("footer.text_start")}
              </Text>
              <Text fz="lg" c="var(--text-on-dark)">
                {tCommon("footer.text_cta")}{" "}
                <Anchor href={`mailto:${dialogueEmail()}`} c="var(--text-on-dark)" td="underline" fw="bold">
                  {dialogueEmail()}
                </Anchor>
              </Text>
            </Stack>
            <Title order={3} size="h3" mb="md" c="var(--accent-on-dark)" fw="bold">
              {t("general.root.support.title")}
            </Title>
            <Text fz="lg" mb="lg" c="var(--text-on-dark)">
              {t("general.root.support.subtitle")}
            </Text>
            <Button
              {...filledCtaButtonProps("primary")}
              component="a"
              href="https://www.canucci.com"
              target="_blank"
              rel="noopener noreferrer"
              size="lg"
            >
              {t("general.root.support.button")}
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}

export function GeneralRootFooter() {
  const { t } = useTranslation("pages");

  return (
    <Container size="lg" w="100%">
      <Flex direction={{ base: "column", sm: "row" }} justify="space-between" align="center">
        <Text fz="sm" c="var(--text-on-dark)">
          {`© ${new Date().getFullYear()} ${t("general.root.footer.copyright")}`}
        </Text>
        <Flex gap="md">
          <Anchor component={Link} to="/terms-of-service" fz="sm" c="var(--text-on-dark)">
            {t("general.root.footer.terms_of_service")}
          </Anchor>
          <Anchor component={Link} to="/privacy-policy" fz="sm" c="var(--text-on-dark)">
            {t("general.root.footer.privacy_policy")}
          </Anchor>
        </Flex>
      </Flex>
    </Container>
  );
}

export default GeneralRootPage;
